import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Avatar,
  Tooltip,
  Alert,
  Snackbar,
  CircularProgress,
  InputAdornment,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Block as BlockIcon,
  LockOpen as UnblockIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { apiGet, apiPost, apiPut, apiDelete } from '../api';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [totalUsers, setTotalUsers] = useState(0);
  
  // Dialog states
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState(''); // 'create', 'edit', 'view'
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    isProfileCompleted: true,
    interests: [],
    profilePhoto: '',
    logo: ''
  });
  
  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Stats state
  const [stats, setStats] = useState({
    totalUsers: 0,
    completedProfiles: 0,
    incompleteProfiles: 0,
    blockedUsers: 0,
    activeUsers: 0,
    recentUsers: 0,
    weeklyUsers: 0,
    completionRate: 0
  });

  // Block/Unblock dialog state
  const [blockDialog, setBlockDialog] = useState({
    open: false,
    user: null,
    reason: ''
  });

  // Actions menu state
  const [actionsMenu, setActionsMenu] = useState({
    anchorEl: null,
    user: null
  });

  // Fetch users data
  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        search: searchTerm,
        sortBy,
        sortOrder
      });
      
      console.log('🔍 Fetching users with params:', params.toString());
      const response = await apiGet(`/admin/users?${params}`);
      console.log('📊 Users API response:', response.data);
      if (response.data.status) {
        setUsers(response.data.data.users);
        setTotalUsers(response.data.data.pagination.totalUsers);
      } else {
        console.error('❌ API returned error:', response.data.message);
        showSnackbar(`API Error: ${response.data.message}`, 'error');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Error fetching users', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, sortBy, sortOrder]);

  // Fetch user statistics
  const fetchStats = async () => {
    try {
      console.log('📈 Fetching user stats...');
      const response = await apiGet('/admin/users/stats');
      console.log('📊 Stats API response:', response.data);
      if (response.data.status) {
        setStats(response.data.data);
      } else {
        console.error('❌ Stats API returned error:', response.data.message);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchStats();
  }, [fetchUsers]);

  // Handle search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (page !== 0) setPage(0); // Reset to first page when searching
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm, page]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSortChange = (event) => {
    setSortBy(event.target.value);
  };

  const handleSortOrderChange = (event) => {
    setSortOrder(event.target.value);
  };

  const handleRefresh = () => {
    fetchUsers();
    fetchStats();
  };

  const handleOpenDialog = (type, user = null) => {
    setDialogType(type);
    setSelectedUser(user);
    
    if (type === 'create') {
      setFormData({
        name: '',
        email: '',
        phone: '',
        password: '',
        isProfileCompleted: true,
        interests: [],
        profilePhoto: '',
        logo: ''
      });
    } else if (type === 'edit' && user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        password: '',
        isProfileCompleted: user.isProfileCompleted || false,
        interests: user.interests || [],
        profilePhoto: user.profilePhoto || '',
        logo: user.logo || ''
      });
    } else if (type === 'view' && user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        phone: user.phone || '',
        password: '',
        isProfileCompleted: user.isProfileCompleted || false,
        interests: user.interests || [],
        profilePhoto: user.profilePhoto || '',
        logo: user.logo || ''
      });
    }
    
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      password: '',
      isProfileCompleted: true,
      interests: [],
      profilePhoto: '',
      logo: ''
    });
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    try {
      let response;
      
      if (dialogType === 'create') {
        response = await apiPost('/api/v1/admin/users', formData);
      } else if (dialogType === 'edit' && selectedUser) {
        response = await apiPut(`/api/v1/admin/users/${selectedUser._id}`, formData);
      }
      
      if (response.data.status) {
        showSnackbar(`User ${dialogType === 'create' ? 'created' : 'updated'} successfully`);
        handleCloseDialog();
        fetchUsers();
        fetchStats();
      }
    } catch (error) {
      console.error(`Error ${dialogType === 'create' ? 'creating' : 'updating'} user:`, error);
      showSnackbar(`Error ${dialogType === 'create' ? 'creating' : 'updating'} user`, 'error');
    }
  };

  const handleDelete = async (user) => {
    if (window.confirm(`Are you sure you want to delete user "${user.name || user.phone}"?`)) {
      try {
        const response = await apiDelete(`/admin/users/${user._id}`);
        if (response.data.status) {
          showSnackbar('User deleted successfully');
          fetchUsers();
          fetchStats();
        }
      } catch (error) {
        console.error('Error deleting user:', error);
        showSnackbar('Error deleting user', 'error');
      }
    }
  };

  const handleBlockUser = (user) => {
    setBlockDialog({
      open: true,
      user: user,
      reason: ''
    });
  };

  const handleUnblockUser = async (user) => {
    if (window.confirm(`Are you sure you want to unblock user "${user.name || user.phone}"?`)) {
      try {
        const response = await apiPost(`/admin/users/${user._id}/unblock`);
        if (response.data.status) {
          showSnackbar('User unblocked successfully');
          fetchUsers();
          fetchStats();
        }
      } catch (error) {
        console.error('Error unblocking user:', error);
        showSnackbar('Error unblocking user', 'error');
      }
    }
  };

  const handleBlockConfirm = async () => {
    try {
      const response = await apiPost(`/admin/users/${blockDialog.user._id}/block`, {
        reason: blockDialog.reason
      });
      if (response.data.status) {
        showSnackbar('User blocked successfully');
        setBlockDialog({ open: false, user: null, reason: '' });
        fetchUsers();
        fetchStats();
      }
    } catch (error) {
      console.error('Error blocking user:', error);
      showSnackbar('Error blocking user', 'error');
    }
  };

  const handleBlockDialogClose = () => {
    setBlockDialog({ open: false, user: null, reason: '' });
  };

  const handleActionsMenuOpen = (event, user) => {
    setActionsMenu({
      anchorEl: event.currentTarget,
      user: user
    });
  };

  const handleActionsMenuClose = () => {
    setActionsMenu({
      anchorEl: null,
      user: null
    });
  };

  const handleMenuAction = (action, user) => {
    handleActionsMenuClose();
    
    switch (action) {
      case 'view':
        handleOpenDialog('view', user);
        break;
      case 'edit':
        handleOpenDialog('edit', user);
        break;
      case 'block':
        handleBlockUser(user);
        break;
      case 'unblock':
        handleUnblockUser(user);
        break;
      case 'delete':
        handleDelete(user);
        break;
      default:
        break;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Users Management
      </Typography>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.totalUsers}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Total Users
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.completedProfiles}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completed
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CancelIcon color="warning" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.incompleteProfiles}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Incomplete
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <BlockIcon color="error" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.blockedUsers}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Blocked
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.activeUsers}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Active
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon color="info" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.weeklyUsers}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    This Week
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon color="secondary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.recentUsers}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Last 30 Days
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CheckCircleIcon color="primary" sx={{ mr: 1 }} />
                <Box>
                  <Typography variant="h6">{stats.completionRate}%</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Completion Rate
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Controls */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            placeholder="Search users..."
            value={searchTerm}
            onChange={handleSearchChange}
            size="small"
            sx={{ minWidth: 200 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Sort By</InputLabel>
            <Select value={sortBy} onChange={handleSortChange} label="Sort By">
              <MenuItem value="createdAt">Created Date</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="phone">Phone</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="lastLoginAt">Last Login</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Order</InputLabel>
            <Select value={sortOrder} onChange={handleSortOrderChange} label="Order">
              <MenuItem value="desc">Desc</MenuItem>
              <MenuItem value="asc">Asc</MenuItem>
            </Select>
          </FormControl>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog('create')}
          >
            Add User
          </Button>
          
          <Button
            variant="outlined"
            onClick={async () => {
              try {
                console.log('🧪 Testing admin login...');
                const response = await apiPost('/admin/login', {
                  email: 'admin@example.com',
                  password: 'admin123'
                });
                console.log('📊 Login response:', response.data);
                if (response.data.status) {
                  localStorage.setItem('adminToken', response.data.data.token);
                  showSnackbar('Login successful! Token stored.', 'success');
                  fetchUsers();
                  fetchStats();
                } else {
                  showSnackbar(`Login failed: ${response.data.message}`, 'error');
                }
              } catch (error) {
                console.error('❌ Login test error:', error);
                showSnackbar(`Login test error: ${error.message}`, 'error');
              }
            }}
          >
            Test Login
          </Button>
          
          <IconButton onClick={handleRefresh} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Paper>

      {/* Users Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Profile Status</TableCell>
                <TableCell>Account Status</TableCell>
                <TableCell>Interests</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ mr: 2, bgcolor: 'primary.main' }}>
                          {user.profilePhoto ? (
                            <img
                              src={user.profilePhoto}
                              alt={user.name}
                              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            />
                          ) : (
                            getInitials(user.name)
                          )}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {user.name || 'No Name'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ID: {user._id.slice(-8)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        {user.phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                            <PhoneIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography variant="body2">{user.phone}</Typography>
                          </Box>
                        )}
                        {user.email && (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <EmailIcon fontSize="small" sx={{ mr: 1 }} />
                            <Typography variant="body2">{user.email}</Typography>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isProfileCompleted ? 'Completed' : 'Incomplete'}
                        color={user.isProfileCompleted ? 'success' : 'warning'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.isBlocked ? 'Blocked' : 'Active'}
                        color={user.isBlocked ? 'error' : 'success'}
                        size="small"
                        icon={user.isBlocked ? <BlockIcon /> : <CheckCircleIcon />}
                      />
                      {user.isBlocked && user.blockReason && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          Reason: {user.blockReason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {user.interests && user.interests.length > 0 ? (
                          user.interests.slice(0, 2).map((interest, index) => (
                            <Chip
                              key={index}
                              label={interest.title}
                              size="small"
                              variant="outlined"
                            />
                          ))
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No interests
                          </Typography>
                        )}
                        {user.interests && user.interests.length > 2 && (
                          <Chip
                            label={`+${user.interests.length - 2}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(user.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.lastLoginAt ? formatDate(user.lastLoginAt) : 'Never'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Actions">
                        <IconButton
                          size="small"
                          onClick={(e) => handleActionsMenuOpen(e, user)}
                          sx={{ 
                            '&:hover': { 
                              backgroundColor: 'action.hover' 
                            } 
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalUsers}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* User Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {dialogType === 'create' && 'Create New User'}
          {dialogType === 'edit' && 'Edit User'}
          {dialogType === 'view' && 'User Details'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                disabled={dialogType === 'view'}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={(e) => handleFormChange('phone', e.target.value)}
                disabled={dialogType === 'view'}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => handleFormChange('email', e.target.value)}
                disabled={dialogType === 'view'}
              />
            </Grid>
            {dialogType === 'create' && (
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => handleFormChange('password', e.target.value)}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Profile Status</InputLabel>
                <Select
                  value={formData.isProfileCompleted}
                  onChange={(e) => handleFormChange('isProfileCompleted', e.target.value)}
                  disabled={dialogType === 'view'}
                  label="Profile Status"
                >
                  <MenuItem value={true}>Completed</MenuItem>
                  <MenuItem value={false}>Incomplete</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Profile Photo URL"
                value={formData.profilePhoto}
                onChange={(e) => handleFormChange('profilePhoto', e.target.value)}
                disabled={dialogType === 'view'}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Logo URL"
                value={formData.logo}
                onChange={(e) => handleFormChange('logo', e.target.value)}
                disabled={dialogType === 'view'}
              />
            </Grid>
            {dialogType === 'view' && selectedUser && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom>
                  User ID: {selectedUser._id}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Created: {formatDate(selectedUser.createdAt)}
                </Typography>
                <Typography variant="subtitle2" gutterBottom>
                  Updated: {formatDate(selectedUser.updatedAt)}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>
            {dialogType === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {dialogType !== 'view' && (
            <Button onClick={handleSubmit} variant="contained">
              {dialogType === 'create' ? 'Create' : 'Update'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Actions Menu */}
      <Menu
        anchorEl={actionsMenu.anchorEl}
        open={Boolean(actionsMenu.anchorEl)}
        onClose={handleActionsMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            minWidth: 160,
            '& .MuiMenuItem-root': {
              px: 2,
              py: 1,
            },
          },
        }}
      >
        <MenuItem onClick={() => handleMenuAction('view', actionsMenu.user)}>
          <ListItemIcon>
            <ViewIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>View Details</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => handleMenuAction('edit', actionsMenu.user)}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit User</ListItemText>
        </MenuItem>
        
        {actionsMenu.user?.isBlocked ? (
          <MenuItem 
            onClick={() => handleMenuAction('unblock', actionsMenu.user)}
            sx={{ color: 'success.main' }}
          >
            <ListItemIcon>
              <UnblockIcon fontSize="small" color="success" />
            </ListItemIcon>
            <ListItemText>Unblock User</ListItemText>
          </MenuItem>
        ) : (
          <MenuItem 
            onClick={() => handleMenuAction('block', actionsMenu.user)}
            sx={{ color: 'warning.main' }}
          >
            <ListItemIcon>
              <BlockIcon fontSize="small" color="warning" />
            </ListItemIcon>
            <ListItemText>Block User</ListItemText>
          </MenuItem>
        )}
        
        <Divider />
        
        <MenuItem 
          onClick={() => handleMenuAction('delete', actionsMenu.user)}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete User</ListItemText>
        </MenuItem>
      </Menu>

      {/* Block User Dialog */}
      <Dialog open={blockDialog.open} onClose={handleBlockDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          Block User: {blockDialog.user?.name || blockDialog.user?.phone}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Reason for blocking (optional)"
              multiline
              rows={3}
              value={blockDialog.reason}
              onChange={(e) => setBlockDialog(prev => ({ ...prev, reason: e.target.value }))}
              placeholder="Enter reason for blocking this user..."
            />
            <Alert severity="warning" sx={{ mt: 2 }}>
              Blocking a user will prevent them from accessing the application. This action can be reversed later.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBlockDialogClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleBlockConfirm} 
            variant="contained" 
            color="error"
            disabled={!blockDialog.user}
          >
            Block User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
